
import router from '@/router/index';
import UserRow from '@/components/UserRow.vue';
import EazyHeading from '@/components/EazyHeading.vue';
import requestAPI from '@/logic/requestBeApi';
import store from '@/store/index';
import SearchInput from '@/components/Inputs/SearchInput.vue';

export default {
  name: 'ClientCandidates',

  components: {
    UserRow,
    EazyHeading,
    SearchInput,
  },

  data(): Record<string, unknown> {
    return {
      router,
      device: store.state.device,
      userInput: '',
      candidates: '',
      fetchingCandidates: false,
      filteredCandidates: '',
      searchApplied: false,
      noResults: false,
      value: '',
      projects: new Set(),
      selectedProject: '',
      candidatesFiltered: false,
      clients: [],
      selectedClient: '',
    };
  },

  computed: {
    roleType(): string {
      return store.state.roleType;
    },

    isClientReadOnly(): boolean {
      return localStorage.getItem('clientRoleType') === 'clientReadOnly';
    },

    cameFromNewProject(): string | undefined {
      return this.$route.query.from_new_project;
    },

    listOfCandsWithoutAnyQueries(): boolean {
      return !this.filteredCandidates && !this.searchApplied && !this.candidatesFiletered;
    },
  },

  watch: {
    userInput(): void {
      this.searchApplied = !!this.userInput;
      this.filteredCandidates = this.candidates.filter(
        (cand: { first_name: string; surname: string }) => {
          if (cand.first_name.toLowerCase().includes(this.userInput.toLowerCase())) return cand;
          if (cand.surname.toLowerCase().includes(this.userInput.toLowerCase())) return cand;
          else return;
        },
      );
    },

    filteredCandidates(): void {
      this.noResults = this.searchApplied && !this.filteredCandidates.length;
    },
  },

  async created(): Promise<void> {
    await this.getCandidates();
  },

  methods: {
    handleUserInput(payload: unknown): void {
      this.userInput = payload;
    },

    onProjectChange(): void {
      this.selectedClient = '';
      this.getGroupedCandidatesByProject(this.selectedProject);
    },

    onProjectClear(): void {
      this.selectedProject = '';
    },

    onClientChange(): void {
      this.selectedProject = '';
      this.getFilteredCandidatesByClient(this.selectedClient);
    },

    onClientClear(): void {
      this.selectedClient = '';
    },

    async fetchAssessmentRequestName(requestUrl: string): Promise<string | null> {
      try {
        const response = await requestAPI({
          url: requestUrl,
          method: 'GET',
        });
        if (!response.ok) {
          throw new Error('Failed to fetch assessment request');
        }
        const data = await response.json();
        return data.name || null;
      } catch (error) {
        this.$message.error(this.$t('someErrorOccurred'));
        return null;
      }
    },

    async fetchClientName(requestUrl: string): Promise<{ id: string; name: string } | null> {
      try {
        const response = await requestAPI({
          url: requestUrl,
          method: 'GET',
        });
        if (!response.ok) {
          throw new Error('Failed to fetch assessment request');
        }
        const data = await response.json();
        const { id, first_name, surname } = data.client;
        const clientName = first_name + ' ' + surname;

        return { id, name: clientName };
      } catch (error) {
        this.$message.error(this.$t('someErrorOccurred'));
        return null;
      }
    },

    async getGroupedCandidatesByProject(projectUrl): Promise<void> {
      if (this.selectedProject) {
        const filteredCandidates = this.candidates.filter((candidate) => {
          return candidate.assessment_requests.some((requestUrl) => requestUrl === projectUrl);
        });
        this.filteredCandidates = filteredCandidates;
      } else {
        this.filteredCandidates = this.candidates;
      }
    },

    async getFilteredCandidatesByClient(selectedClient): Promise<void> {
      if (this.selectedClient) {
        const filteredCandidates = [];
        for (const candidate of this.candidates) {
          let hasMatchingAssessmentRequest = false;
          for (const requestUrl of candidate.assessment_requests) {
            const client = await this.fetchClientName(requestUrl);
            if (client.id && client.id === selectedClient) {
              hasMatchingAssessmentRequest = true;
              break;
            }
          }
          if (hasMatchingAssessmentRequest) {
            filteredCandidates.push(candidate);
          }
        }
        this.filteredCandidates = filteredCandidates;
      } else {
        this.filteredCandidates = this.candidates;
      }
    },

    async getCandidates(): Promise<void> {
      this.fetchingCandidates = true;

      const resp = await requestAPI({ url: '/api/v1/candidates?per_page=100000', method: 'GET' });

      if (resp.ok) {
        const data = await resp.json();
        this.candidates = data;
        this.candidates = this.candidates.map((cand) => {
          if (!cand.profile_picture) cand.profile_picture = {};
          return cand;
        });

        if (this.roleType === 'assessorPlus') {
          await this.fetchProjects();
          await this.fetchClientNames();
        }
      }
      this.fetchingCandidates = false;
    },

    async fetchProjects(): Promise<void> {
      const assessmentRequestUrls = this.candidates.flatMap(
        (candidate) => candidate.assessment_requests,
      );

      const uniqueProjects = new Map();

      for (const requestUrl of assessmentRequestUrls) {
        const requestName = await this.fetchAssessmentRequestName(requestUrl);
        if (requestName) {
          uniqueProjects.set(requestUrl, { name: requestName, url: requestUrl });
        }
      }

      this.projects = Array.from(uniqueProjects.values());
    },

    async fetchClientNames(): Promise<void> {
      const assessmentRequestUrls = this.candidates.flatMap(
        (candidate) => candidate.assessment_requests,
      );

      const uniqueClients = new Map();

      for (const requestUrl of assessmentRequestUrls) {
        const clientData = await this.fetchClientName(requestUrl);
        if (clientData && !uniqueClients.has(clientData.id)) {
          uniqueClients.set(clientData.id, clientData);
        }
      }
      this.clients = Array.from(uniqueClients.values());
    },

    addCandidate(): void {
      if (this.roleType !== 'assessorPlus') {
        router.push({
          path: '/client-add-candidate',
          query: { from_new_project: this.cameFromNewProject },
        });
      } else {
        router.push({
          path: '/assessor-add-candidate',
          query: { from_new_project: this.cameFromNewProject },
        });
      }
    },

    addCandidates(): void {
      if (this.roleType !== 'assessorPlus') {
        router.push({
          path: '/client-add-candidates',
          query: { from_new_project: this.cameFromNewProject },
        });
      } else {
        router.push({
          path: '/assessor-add-candidates',
          query: { from_new_project: this.cameFromNewProject },
        });
      }
    },
  },
};
